import { Injectable, inject } from '@angular/core';
import { ConfigService } from './config.service';
import { Observable, filter, map, switchMap, take, of, Subject } from 'rxjs';

import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  ApiService,
  calendarOAuthLogin,
  CalendarServerToken,
  CueResponse,
  ExternalLoginCA,
  logoutEWS,
  oAuthLogin,
  oAuthLogout,
  oAuthRefreshToken,
  oAuthToken,
  refresh,
  ServerTokenResponse,
} from '@cue/api';
import { clearCurrentUser } from '../actions';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private configService = inject(ConfigService);
  private store = inject<Store<any>>(Store<any>);
  private router = inject(Router);
  private apiService = inject(ApiService);
  _apiTokenKey = 'api_token';
  _apiRefreshTokenKey = 'api_refresh_token';
  _profilePairingKey = 'external_login_profile_pairing';

  private oauthToken = null;

  getCalendarTokenFromServer(
    code: string,
    redirectUri: string,
    scope: string,
    connectionAgentId: number
  ): Observable<CalendarServerToken> {
    return this.apiService.call(calendarOAuthLogin(code, redirectUri, scope, connectionAgentId));
  }

  loginWithExternalIdentity(connectionAgent: ExternalLoginCA, pairFromProfile = false) {
    const redirectUri = location.origin + '/account/oauth-callback';
    const prompt = connectionAgent.prompt ?? 'consent';
    let scope: string;
    let url: string;

    if (connectionAgent.serverTypeId === 1) {
      // Office 365
      scope = 'openid profile';
      url = `https://login.microsoftonline.com/${connectionAgent.tenantId}/oauth2/v2.0/authorize?client_id=${
        connectionAgent.clientId
      }&prompt=${prompt}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;
    } else if (connectionAgent.serverTypeId === 4) {
      // google
      scope = 'openid profile email';
      url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
        connectionAgent.clientId
      )}&access_type=offline&response_type=code&prompt=${prompt}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&scope=${encodeURIComponent(scope)}`;
    } else {
      return;
    }

    localStorage.setItem('connectionAgentId', connectionAgent.id.toString());
    localStorage.setItem('scope', scope);
    localStorage.setItem('oauthRedirectUrl', redirectUri);

    if (pairFromProfile) {
      sessionStorage.setItem(this._profilePairingKey, 'true');
      sessionStorage.setItem('redirect_url', '/profile?tab=linked');
    } else {
      sessionStorage.removeItem(this._profilePairingKey);
    }

    location.href = url;
  }

  isProfileExternalLoginPairing() {
    return sessionStorage.getItem(this._profilePairingKey) === 'true';
  }

  clearProfileExternalLoginPairing() {
    sessionStorage.removeItem(this._profilePairingKey);
  }

  getTokenFromServer(code: string): Observable<ServerTokenResponse> {
    return this.apiService.call(oAuthLogin(code, this.configService.value.redirectUri, this.configService.value.scope));
  }

  getOAuthToken() {
    return this.oauthToken;
  }

  setOAuthToken(token: string) {
    this.oauthToken = token;
  }

  getOAuthTokenFromTheServer(): Observable<CueResponse<string>> {
    return this.apiService.call(oAuthToken());
  }

  refreshOAuthTokenFromTheServer(): Observable<CueResponse<string>> {
    return this.apiService.call(oAuthRefreshToken(this.configService.value.scope));
  }

  getApiToken() {
    return localStorage.getItem(this._apiTokenKey);
  }

  getApiRefreshToken() {
    return localStorage.getItem(this._apiRefreshTokenKey);
  }

  saveApiRefreshToken(refreshToken: string) {
    localStorage.setItem(this._apiRefreshTokenKey, refreshToken);
  }

  #apiTokenChanged = new Subject<void>();
  apiTokenChanged = this.#apiTokenChanged.asObservable();

  saveApiToken(token: string) {
    localStorage.setItem(this._apiTokenKey, token);
    this.#apiTokenChanged.next();
  }

  removeApiToken() {
    console.log('removeApiToken!!!');
    localStorage.removeItem(this._apiTokenKey);
    this.#apiTokenChanged.next();
  }

  removeApiRefreshToken() {
    localStorage.removeItem(this._apiRefreshTokenKey);
  }

  redirectToAuthorize() {
    const prompt = this.configService.value.prompt ?? 'consent';

    if (this.configService.isO365()) {
      const url = `https://login.microsoftonline.com/${this.configService.value.tenantId}/oauth2/v2.0/authorize?client_id=${
        this.configService.value.clientId
      }&prompt=${prompt}&response_type=code&redirect_uri=${encodeURIComponent(
        this.configService.value.redirectUri
      )}&scope=${this.configService.value.scope}`;

      location.href = url;
    }
    if (this.configService.isGspace()) {
      const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
        this.configService.value.clientId
      )}&access_type=offline&response_type=code&prompt=${prompt}&redirect_uri=${encodeURIComponent(
        this.configService.value.redirectUri
      )}&scope=${encodeURIComponent(this.configService.value.scope)}`;
      location.href = url;
    }
  }

  logout() {
    const redirdctUrl =
      location.pathname != '' &&
      location.pathname != '/account/login' &&
      location.pathname != '/account/email-link' &&
      location.pathname != '/account/oauth-callback' &&
      location.pathname != '/account/guest'
        ? location.pathname + location.search
        : undefined;
    if (redirdctUrl !== undefined) {
      sessionStorage.setItem('redirect_url', redirdctUrl);
    }
    this.removeApiToken();
    this.removeApiRefreshToken();

    this.router.navigate(['/', 'account', 'login'], {
      queryParams: {
        return_url: redirdctUrl,
      },
    });
  }

  refreshTokenRequest() {
    const accessToken = this.configService.isEWS() ? this.getApiToken() : null;
    const refreshToken = this.getApiRefreshToken();
    return this.apiService.call(refresh(refreshToken, accessToken));
  }

  logoutAndLogoutFromServer(): Observable<any> {
    if (this.configService.isEWS()) {
      return this.apiService.call(logoutEWS()).pipe(
        switchMap((_) => {
          this.removeApiToken();
          this.removeApiRefreshToken();
          this.store.dispatch(clearCurrentUser());
          return this.router.navigate(['/account/login']);
        })
      );
    } else if (this.configService.isTouchoneCalendar()) {
      this.removeApiToken();
      this.removeApiRefreshToken();
      this.store.dispatch(clearCurrentUser());
      return of(this.router.navigate(['/account/login']));
    } else {
      return this.apiService.call(oAuthLogout()).pipe(
        switchMap((_) => {
          this.removeApiRefreshToken();
          this.removeApiToken();
          this.oauthToken = null;
          this.store.dispatch(clearCurrentUser());
          return this.router.navigate(['/', 'account', 'login']);
        })
      );
    }
  }

  getName(): string {
    // ne uplne idealni, ale melo by to fungovat
    let name: string = '';
    this.store
      .select((x) => x.shared.currentUser)
      .pipe(
        filter((x) => !x.loading && !x.error),
        map((x) => x.data!.name),
        take(1)
      )
      .subscribe((x) => (name = x));
    return name;
  }

  getEmail(): string {
    let email = '';
    this.store
      .select((x) => x.shared.currentUser)
      .pipe(
        filter((x) => !x.loading && !x.error),
        map((x) => x.data!.email),
        take(1)
      )
      .subscribe((x) => (email = x));
    return email;
  }

  getUsername(): string {
    let username = '';
    this.store
      .select((x) => x.shared.currentUser)
      .pipe(
        filter((x) => !x.loading && !x.error),
        map((x) => x.data!.username),
        take(1)
      )
      .subscribe((x) => (username = x));
    return username;
  }
}
